/**
 * A disposable native copy of a scene, played from the start on an explicit clock. Every review function works on one, so the source
 * scene (transforms, visibility, state indices, timers, selection, play mode) is never touched.
 */
import { NullEngine } from '@babylonjs/core/Engines/nullEngine';
import type { SceneObject } from '../../core/SceneObject';
import type { TraceScene } from '../../core/TraceScene';
import { awaitLayoutQuiescence } from '../../export/settle';
import { copyNativeRuntime } from '../inspection/native-copy';
import type { NativeCopyOptions } from '../inspection/native-copy';
import { useExplicitClock } from '../inspection/runtime-probe';

/** Fixed step the copy is advanced by. 30 Hz is plenty for a 10 Hz motion table and half the cost of 60. */
export const FRAME_MS = 1000 / 30;

/** A wall-clock allowance for one review call. */
export interface Budget { left(): number; expired(): boolean }

export function makeBudget(ms: number | undefined, fallback: number): Budget {
    const end = performance.now() + (ms !== undefined && ms >= 0 ? ms : fallback);
    return { left: () => end - performance.now(), expired: () => performance.now() >= end };
}

export interface Run {
    trace: TraceScene;
    /** Source object to its copy. */
    copy: Map<SceneObject, SceneObject>;
    /** Copy paths of the objects the caller asked about, or null for every object. */
    scope: Set<string> | null;
    /** Elapsed play time, milliseconds. */
    nowMs: number;
    /** The budget ran out while playing: `advance` stopped early and `nowMs` is short of what was asked. */
    truncated: boolean;
    /** Play for `ms` of scene time, rendering fixed frames. */
    advance(ms: number): Promise<void>;
    advanceTo(ms: number): Promise<void>;
    dispose(): void;
}

/**
 * Open a copy. With a `budget`, resolves null when the copy is not ready in time (its late result is disposed), so one stuck asset
 * cannot hold the call past its cap.
 */
export async function openRun(source: TraceScene, objects?: readonly SceneObject[], budget?: Budget): Promise<Run | null> {
    if (!budget) return openRunNow(source, { objects, copy: REVIEW_COPY });
    const pending = openRunNow(source, { objects, budget, copy: REVIEW_COPY });
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<null>(resolve => { timer = setTimeout(() => resolve(null), Math.max(0, budget.left())); });
    try {
        const got = await Promise.race([pending, timeout]);
        if (!got) pending.then(late => late.dispose(), () => undefined);
        return got;
    } finally { clearTimeout(timer); }
}

// Remote models are not fetched: a stand-in box of the same bounds keeps layout and clicks honest and a dead URL costs nothing.
const REVIEW_COPY: NativeCopyOptions = { placeholderRemote: true };

/** `openRun` without the review defaults: the caller chooses how the copy is made, and no budget ends a call early. */
export async function openRunNow(source: TraceScene, { objects, budget, copy = {} }: { objects?: readonly SceneObject[]; budget?: Budget; copy?: NativeCopyOptions } = {}): Promise<Run> {
    const runtime = await copyNativeRuntime(source, copy);
    try {
        const { trace } = runtime;
        const clock = useExplicitClock(trace);
        await awaitLayoutQuiescence(trace);
        trace.setPlayMode(true);
        const scene = trace.getBabylonScene(), engine = scene.getEngine();
        if (!(engine instanceof NullEngine)) throw new Error('Review needs a NullEngine copy');
        let delta = 0;
        Object.defineProperty(engine, 'getDeltaTime', { configurable: true, value: () => delta });
        // Not stepRuntime: a review stops mid-window when its wall-clock budget runs out, steps at 30 Hz, and takes no
        // per-frame snapshot of every object (its callers sample what they need between advances).
        const run: Run = {
            trace, copy: runtime.copy, nowMs: 0, scope: null, truncated: false,
            async advance(ms) {
                let left = Math.max(0, ms);
                while (left > 1e-6) {
                    if (budget?.expired()) { run.truncated = true; return; }
                    delta = Math.min(FRAME_MS, left);
                    clock.nowMs += delta;
                    run.nowMs = clock.nowMs;
                    scene.render();
                    await Promise.resolve();
                    left -= delta;
                }
            },
            advanceTo(ms) { return run.advance(ms - run.nowMs); },
            dispose: runtime.dispose,
        };
        if (objects) run.scope = new Set(objects.map(o => runtime.copy.get(o)?.getPath()).filter((p): p is string => !!p));
        return run;
    } catch (error) { runtime.dispose(); throw error; }
}

/** Run `fn` on a fresh copy and always dispose it. Null when the copy was not ready within `budget`. */
export async function withRun<T>(source: TraceScene, objects: readonly SceneObject[] | undefined, budget: Budget | undefined, fn: (run: Run) => Promise<T>): Promise<T | null> {
    const run = await openRun(source, objects, budget);
    if (!run) return null;
    try { return await fn(run); } finally { run.dispose(); }
}
